/** @jsx jsx */
import { useEffect } from 'react';
import { jsx } from '@compiled/react';
import { Box, xcss, type XCSS } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useHierarchyActions } from '../../controllers/hierarchy/index.tsx';
import type { CellBorderStyle } from '../../common/types.tsx';

export type RenderTableCell = (args: {
	key: string;
	colSpan: number;
	border: Array<XCSS | false | undefined>;
	children: JSX.Element;
	stickyOffsets?: number;
	isColumnSticky?: boolean;
}) => JSX.Element;

export type StickyColumnConfig = {
	stickyOffsets?: number;
	isColumnSticky?: boolean;
};
type Props = {
	itemId: string;
	groupId?: string;
	isFixedTableLayoutEnabled: boolean;
	columnsCount: number;
	stickyColumnConfigs?: StickyColumnConfig[];
	renderTableCell: RenderTableCell;
	cellBorderStyle?: CellBorderStyle;
};

const LoadingIssueRow = ({
	itemId,
	groupId,
	columnsCount,
	stickyColumnConfigs,
	isFixedTableLayoutEnabled,
	cellBorderStyle = 'FULL_BORDER',
	renderTableCell,
}: Props) => {
	const { setItemIsLoading } = useHierarchyActions();

	useEffect(() => {
		setItemIsLoading({
			itemId,
			groupId,
			isLoading: true,
		});

		return () => {
			setItemIsLoading({
				itemId,
				groupId,
				isLoading: false,
			});
		};
	}, [itemId, groupId, setItemIsLoading]);

	const columns = Array.from({ length: columnsCount });

	const colSpans = columns.map((_, index) => {
		const isLastColumn = index === columns.length - 1;
		return 1 + (isLastColumn && isFixedTableLayoutEnabled ? 1 : 0);
	});

	return (
		<Box as="tr" xcss={[fg('issue_table_frozen_columns') ? wrapperStyles : wrapperOldStyles]}>
			{columns.map((_, i) =>
				renderTableCell({
					key: `${itemId}-${i}`,
					colSpan: colSpans[i],
					border: [
						baseCellBorderStyles,
						cellBorderLocationStyleMap[cellBorderStyle],
						i === columnsCount - 1 && lastCellBorderStyles,
					],
					children: <Box xcss={loadingCellStyles} />,
					...(fg('issue_table_frozen_columns') && {
						isColumnSticky: stickyColumnConfigs?.[i].isColumnSticky,
						stickyOffsets: stickyColumnConfigs?.[i].stickyOffsets,
					}),
				}),
			)}
		</Box>
	);
};

export default LoadingIssueRow;

const wrapperOldStyles = xcss({ height: '40px' });
const wrapperStyles = xcss({ height: '40px', background: token('elevation.surface') });

const lastCellBorderStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: '0',
});

const baseCellBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderBottomWidth: '0',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderLeftWidth: '0',
});

const fullCellBorderStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: 'border.width',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: 'border.width',
});

const noColumnCellBorderStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: 'border.width',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: '0',
});

const noRowCellBorderStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: '0',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderRightWidth: 'border.width',
});

const cellBorderLocationStyleMap = {
	FULL_BORDER: fullCellBorderStyles,
	NO_COLUMN_BORDER: noColumnCellBorderStyles,
	ROW_BORDER_FOR_EXPANDED_TOP_LEVEL_ONLY: noRowCellBorderStyles,
};

const loadingCellStyles = xcss({
	backgroundColor: 'color.background.neutral',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
	padding: 'space.150',
	width: '100%',
});
